import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '../../Services/shared.service';
import { debounceTime, distinctUntilChanged, merge, Subject, takeUntil } from 'rxjs';
import { APP_BUILDER_PERMISSIONS, FONTS, PERMISSIONS_ACTIONS } from 'shared-library';
import { ToastrService } from 'ngx-toastr';
import { VlDataService, VlHttpService, HTTPAPIConfig, APIConfigMapping, isNullOrUndefined } from 'shared-library';

@Component({
  selector: 'lib-mobi-theme',
  templateUrl: './mobi-theme.component.html',
  styleUrls: ['./mobi-theme.component.scss']
})
export class MobiThemeComponent implements OnInit,OnDestroy{
  themeColors!: FormGroup;
  fonts:any[]=[];
  visible:boolean=false;
  displayTheme = false;
  allThemes:any[] = [];
  cancelEnable:boolean=false;
  spinnerActive:boolean=false;
  destroy$:Subject<void> = new Subject<void>;
  appPermissions = APP_BUILDER_PERMISSIONS;
  permissionsActions = PERMISSIONS_ACTIONS;

  constructor( private _formBuilder: FormBuilder,
    private sharedService:SharedService,
    private dataService: VlDataService,
    private httpService: VlHttpService,
    private toaster:ToastrService
    ) {

  }
  
  ngOnInit(): void {
    this.spinnerActive=true;
    this.displayTheme = false;
    this.formbuilder();
    this.getAllThemes();
  }

  formbuilder() {
    this.themeColors = this._formBuilder.group({
      primaryColor: "#0061A6",
      secondaryColor: "#6C5778",
      defaultBackgroundColor: "#FAF9FD",
      defaultTextColor: "#45474A",
      themeName: 'M3 Baseline Light',
      headingFont: ["", Validators.required],
      bodyFont: ["", Validators.required]
    });
  }

  getColorValue(controlName: string) {
    return this.themeColors.get(controlName)?.value || "#000000";
  }

  getChangedColorValue(colorName:string) {
    this.sharedService.setThemesData(colorName,this.getColorValue(colorName));
  }

  changesSubscribe(){
    merge(
      this.themeColors.valueChanges.pipe(debounceTime(100), distinctUntilChanged()),
    ).pipe(takeUntil(this.destroy$)).subscribe((formValue) => {
      this.sharedService.updateWholeTheme({...this.themeColors.getRawValue()})
    });
  }
  
  updateThemeData(){
    let userDetails:any=JSON.parse(localStorage.getItem("userInfo") || '');
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['EDIT_THEME']);
    apiConfig.postData = {
      "theme_id": this.sharedService.getApplicationDataById().theme_id,
      "theme_name": this.themeColors.getRawValue()?.themeName,
      "primary_color": this.themeColors.getRawValue().primaryColor,
      "secondary_color": this.themeColors.getRawValue().secondaryColor,
      "background_color": this.themeColors.getRawValue().defaultBackgroundColor,
      "text_color": this.themeColors.getRawValue().defaultTextColor,
      "font_heading": this.themeColors.getRawValue().headingFont,
      "font_body": this.themeColors.getRawValue()?.bodyFont,
      "last_modified_by": userDetails?.userId,
    }

    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
      this.sharedService.setThemeData({...this.themeColors.getRawValue()})
      this.toaster.success('Applied Sucessfully');
      this.cancelEnable=false;
      },
      error: (err: any) => {
        if(err?.status == 403) {
          this.toaster.error('You do not have permission to access this resource.');
        }else{
          this.toaster.error('Something Went Wrong!');
        }
      }
    })
  }
  
  getThemesData(){
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_THEME_BYID']);
    apiConfig.postData = {
      "id": this.sharedService.getApplicationDataById().theme_id,
    }
    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
      this.fonts=FONTS;
      this.themeColors.patchValue({
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        defaultBackgroundColor: data.background_color,
        defaultTextColor: data.text_color,
        themeName:data?.theme_name,
        headingFont:data.font_heading,
        bodyFont:data.font_body
      },{onlySelf:true,emitEvent:false})
      this.sharedService.setThemeData({...this.themeColors.getRawValue()})
      this.visible=true;
      this.spinnerActive=false;
      this.changesSubscribe();
      },
      error: (err: any) => {
        this.spinnerActive=false;
        if(err?.status == 403) {
          this.toaster.error('You do not have permission to access this resource.');
        }
      }
    })
    this.displayTheme = true;
  }

  setThemeData(){
    this.displayTheme = true;
    this.fonts=FONTS;
    this.themeColors.patchValue(this.sharedService.getThemeData());
    this.visible=true;
    this.sharedService.updateWholeTheme({...this.themeColors.getRawValue()});
    this.spinnerActive=false;
    this.changesSubscribe();
  }

  onHeadingFontChange(data:any){
    this.sharedService.setThemesData('headingFont',data?.value);
  }
  
  onBodyFontChange(data:any){
    this.sharedService.setThemesData('bodyFont',data?.value);
  }

  onThemeChange(_event:any){
    const dataTheme = this.sharedService.getThemeData();
    this.cancelEnable = (_event.value === dataTheme.themeName) ? false : true;
    const data = this.allThemes.find((ele: any) => ele['theme_name'] == _event.value);
    const themesColors =  {
      primaryColor: data.primary_color ?? '#0061A6',
      secondaryColor: data.secondary_color ?? '#6C5778', 
      defaultBackgroundColor: data.background_color ?? '#FAF9FD',
      defaultTextColor: data.text_color ?? '#45474A',
      headingFont:data.font_heading,
      bodyFont:data.font_body
    }
    this.themeColors.patchValue(themesColors);
  }

  getAllThemes(){
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(
      APIConfigMapping["GET_THEMES"]
    );
    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
       this.allThemes = data;
        const customThemeObj = {theme_name : 'Custom Theme'}
        this.allThemes.push(customThemeObj);
      },
      complete: () => {
        if(isNullOrUndefined(this.sharedService.getThemeData())){
          this.getThemesData();
        }else{
          this.setThemeData();
        }
      },
      error: (err: any) => {
        this.spinnerActive=false;
        if(err?.status == 403) {
          this.toaster.error('You do not have permission to access this resource.');
        }
      },
    });
  }

  revertBackTheme(){
    this.themeColors.patchValue(this.sharedService.getThemeData());
    this.sharedService.updateWholeTheme({...this.themeColors.getRawValue()})
  }

  ngOnDestroy(): void {
    this.revertBackTheme();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
